/* eslint-disable */
import React from "react";
import {
  Button,
  Grid,
  Group,
  Select,
  TextInput,
  PasswordInput,
  Checkbox,
  Table,
  ScrollArea,
  Box,
  Text,
  Divider,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";

const MODULES = ["Employee", "Holidays", "Leaves", "Events"];
const PERMS = [
  "read",
  "write",
  "create",
  "delete",
  "import",
  "export",
] as const;

const buildInitialPermissions = () =>
  MODULES.reduce<Record<string, Record<string, boolean>>>((acc, m) => {
    acc[m] = PERMS.reduce<Record<string, boolean>>((pAcc, perm) => {
      pAcc[perm] = false;
      return pAcc;
    }, {});
    return acc;
  }, {});

const addUserSchema = z
  .object({
    firstName: z.string().trim().min(1, { message: "Please enter first name" }),
    lastName: z.string().trim().min(1, { message: "Please enter last name" }),
    username: z.string().trim().min(1, { message: "Please enter username" }),
    email: z.string().email({ message: "Invalid email" }),
    password: z
      .string()
      .min(6, { message: "Password must be at least 6 characters" }),
    confirmPassword: z.string().min(1),
    phone: z.string().optional(),
    role: z.string().min(1, { message: "Please select role" }),
    permissions: z.record(z.record(z.boolean())),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof addUserSchema>;

export default function AddUserForm({ onClose }: { onClose: () => void }) {
  const form = useForm<FormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      role: "",
      permissions: buildInitialPermissions(),
    },
    validate: zodResolver(addUserSchema),
    validateInputOnBlur: true,
  });

  const handleSubmit = (values: FormValues) => {
    console.log("submit values", values);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            withAsterisk
            label="First Name"
            placeholder="First name"
            {...form.getInputProps("firstName")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            withAsterisk
            label="Last Name"
            placeholder="Last name"
            {...form.getInputProps("lastName")}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            withAsterisk
            label="User Name"
            placeholder="User name"
            {...form.getInputProps("username")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            withAsterisk
            label="Email"
            placeholder="Email"
            {...form.getInputProps("email")}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <PasswordInput
            withAsterisk
            label="Password"
            placeholder="Password"
            {...form.getInputProps("password")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <PasswordInput
            withAsterisk
            label="Confirm Password"
            placeholder="Confirm Password"
            {...form.getInputProps("confirmPassword")}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label="Phone"
            placeholder="Phone number"
            {...form.getInputProps("phone")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            withAsterisk
            label="Role"
            placeholder="Select"
            data={[
              { value: "superadmin", label: "Super Admin" },
              { value: "admin", label: "Admin" },
              { value: "supervisor", label: "Supervisor" },
              { value: "member", label: "Member" },
              { value: "director", label: "Director" },
              { value: "band_administrator", label: "Band Administrator" },
            ]}
            {...form.getInputProps("role")}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Box
            sx={(theme) => ({
              borderRadius: 4,
              border: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[4]
                  : theme.colors.gray[3]
              }`,
              overflow: "hidden",
            })}
          >
            <Box sx={{ background: "#f1f3f5", padding: "10px 12px" }}>
              <Text weight={700}>Module Permissions</Text>
            </Box>

            <ScrollArea>
              <Table verticalSpacing="sm" striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Module</th>
                    {PERMS.map((p) => (
                      <th
                        key={p}
                        style={{
                          textTransform: "capitalize",
                          textAlign: "center",
                        }}
                      >
                        {p}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map((module) => (
                    <tr key={module}>
                      <td style={{ minWidth: 200 }}>{module}</td>
                      {PERMS.map((perm) => (
                        <td key={perm} style={{ textAlign: "center" }}>
                          <Checkbox
                            {...form.getInputProps(
                              `permissions.${module}.${perm}`,
                              { type: "checkbox" }
                            )}
                            sx={{ display: "inline-block" }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          </Box>
        </Grid.Col>
      </Grid>

      <Divider my="md" />

      <Group position="right" mt={16}>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" color="orange" disabled={!form.isDirty()}>
          Add User
        </Button>
      </Group>
    </form>
  );
}
